import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react'

// Focus state for the compound field primitives, whose focus ring is painted by
// the ROOT while focus happens on the nested TextInput. Neither CSS mechanism
// works on both platforms: gluestack's `data-[focus=true]` never fires for a
// plain TextInput focus, and `focus-within:` reaches the native runtime with an
// empty condition set — which passes, painting the ring permanently (see the
// same NativeWind selector limitation documented in `focusRing.ts`).

const SetFieldFocusedContext = createContext<((focused: boolean) => void) | null>(null)

export const useFieldFocusState = () => {
    const [isFocused, setFocused] = useState(false)
    return { isFocused, setFocused }
}

export const FieldFocusProvider: React.FC<{
    setFocused(focused: boolean): void
    children: ReactNode
}> = ({ setFocused, children }) => (
    <SetFieldFocusedContext.Provider value={setFocused}>{children}</SetFieldFocusedContext.Provider>
)

// The unmount reset is the contract, not a nicety: the focused TextInput can be
// swapped out while the root stays mounted — the Dropdown replaces its search
// field with the picked value the moment an option is chosen — and neither
// platform fires `blur` on unmount, so the root kept painting the ring forever
// (AOS-1345 / AOS-1347).
export const useSetFieldFocused = () => {
    const setFocused = useContext(SetFieldFocusedContext)
    useEffect(() => () => setFocused?.(false), [setFocused])
    return setFocused
}
